import type { ComponentType, ReactNode } from 'react';
import { cx } from '../cx';

export type ArticleCardProps = {
  href: string;
  title: ReactNode;
  /** The category ("Market data", "Guide"). */
  eyebrow?: ReactNode;
  /** Reading time or date, formatted by the caller. */
  meta?: ReactNode;
  /** An Illustration or a real chart thumbnail from the article. Never a stock photo. */
  media?: ReactNode;
  /** One to four lines of summary under the title. */
  description?: ReactNode;
  /** Trailing call to action text ("Read more"). Plain text: the card's single link is the title anchor. */
  action?: ReactNode;
  /** Renders the title anchor with the app's own Link (client navigation) instead of a plain `<a>`. */
  linkComponent?: ComponentType<{ href: string; className?: string; children: ReactNode }>;
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

/** A card for an article: media on a neutral panel (the category is in words, not a colour), the category, the title and reading time. The whole card is the link. */
export function ArticleCard({
  href,
  title,
  eyebrow,
  meta,
  media,
  description,
  action,
  linkComponent,
  headingLevel = 3,
  className,
}: ArticleCardProps) {
  const Heading = `h${headingLevel}` as 'h3';
  const Anchor = linkComponent ?? 'a';
  return (
    <article
      className={cx(
        'group relative flex flex-col overflow-hidden rounded-jc-container border border-jc-card-border bg-jc-surface shadow-jc-sm',
        'transition duration-(--jc-duration-fast) ease-jc-out hover:-translate-y-0.5 hover:shadow-jc-md focus-within:ring-2 focus-within:ring-jc-focus motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        className,
      )}
    >
      {media ? <div className={cx('flex h-36 items-center justify-center', 'bg-jc-elevated')}>{media}</div> : null}
      <div className="flex flex-1 flex-col gap-1.5 p-5">
        {eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{eyebrow}</span> : null}
        <Heading className="type-heading text-jc-ink">
          <Anchor href={href} className="after:absolute after:inset-0 focus-visible:outline-none group-hover:underline">
            {title}
          </Anchor>
        </Heading>
        {description ? <p className="line-clamp-4 text-sm text-jc-ink-secondary">{description}</p> : null}
        {action ? (
          <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-jc-ink">{action}</span>
        ) : null}
        {meta ? <span className="mt-auto pt-2 text-sm text-jc-ink-secondary">{meta}</span> : null}
      </div>
    </article>
  );
}
